import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Hammer from 'react-hammerjs'
import { line, curveCardinal } from 'd3'
import Locale from '../data/localization'
import MapLine from './MapLine'

class MapCanvas extends Component {

    constructor(props) {
        super(props)

        this.state = {
            scale: 1,
            scaleStart: 1,
            scaleLast: 1,

            rotate: 0,
            rotateStart: 0,
            rotateLast: 0,

            centerX: 0,
            centerY: 0,

            offsetX: 0,
            offsetY: 0,
            offsetXStart: 0,
            offsetYStart: 0,

            width: 0,
            height: 0,

            changed: false
        }

        this.boundHandleResize = ::this.handleResize
    }

    static propTypes = {
        path: PropTypes.arrayOf(
            PropTypes.shape({
                x: PropTypes.number,
                y: PropTypes.number
            })
        )
    }

    componentDidMount() {
        const box = this.canvas.getBoundingClientRect()

        const offset = this.getBaseOffset()

        this.setState({
            width: box.width,
            height: box.height,
            offsetX: offset.x,
            offsetY: offset.y,
            offsetXStart: offset.x,
            offsetYStart: offset.y
        })

        window.addEventListener('resize', this.boundHandleResize)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.boundHandleResize)
    }

    getBaseOffset() {
        const box = this.canvas.getBoundingClientRect()

        const points = this.props.path
            .filter(item => item.point)
            .map(item => ({
                x: parseFloat(`${item.point.x}`),
                y: parseFloat(`${item.point.y}`)
            }))

        const coordsX = points.map(point => point.x)
        const coordsY = points.map(point => point.y)

        const min = {
            x: Math.min(...coordsX),
            y: Math.min(...coordsY)
        }

        const max = {
            x: Math.max(...coordsX),
            y: Math.max(...coordsY)
        }

        const boundingBox = {
            x: min.x,
            y: min.y,
            width: max.x - min.x,
            height: max.y - min.y
        }

        let offsetX = 0
        let offsetY = 0

        const startOrientation = Math.abs(points[0].x - max.x) < Math.abs(points[0].x - min.x) ? 'right' : 'left'

        if (boundingBox.width < box.width * .8) {
            offsetX = -(boundingBox.x + boundingBox.width / 2 - box.width / 2)
        } else {
            if (startOrientation === 'right') {
                offsetX = -(max.x - box.width + box.width/4)
            } else {
                offsetX = -(boundingBox.x - box.width / 8)
            }
        }

        if (boundingBox.height < box.height * .8) {
            offsetY = -(boundingBox.y + boundingBox.height / 2 - box.height / 2)
        } else {
            if (startOrientation === 'right') {
                offsetY = -(max.y - box.height + box.height / 4)
            } else {
                offsetY = -(boundingBox.y - box.height / 8)
            }
        }

        return {
            x: offsetX,
            y: offsetY
        }
    }

    handleResize() {
        const box = this.canvas.getBoundingClientRect()

        this.setState({
            width: box.width,
            height: box.height,
            changed: true
        })
    }

    handleDoubleTap() {
        const offset = this.getBaseOffset()

        this.setState({
            scale: 1,
            scaleStart: 1,
            scaleLast: 1,

            rotate: 0,
            rotateStart: 0,
            rotateLast: 0,

            centerX: 0,
            centerY: 0,

            offsetX: offset.x,
            offsetY: offset.y,
            offsetXStart: offset.x,
            offsetYStart: offset.y,

            changed: false
        })
    }

    handlePan(event) {
        this.setState({
            offsetX: this.state.offsetXStart + event.deltaX,
            offsetY: this.state.offsetYStart + event.deltaY,
            changed: true
        })
    }

    handlePanEnd(event) {
        this.setState({
            offsetXStart: this.state.offsetX,
            offsetYStart: this.state.offsetY,
            changed: true
        })
    }

    handleRotateStart(event) {
        this.setState({
            rotateStart: event.rotation,
            rotateLast: this.state.rotate,
            changed: true
        })
    }

    handleRotateMove(event) {
        this.setState({
            rotate: this.state.rotateLast - this.state.rotateStart + event.rotation,
            centerX: event.center.x,
            centerY: event.center.y,
            changed: true
        })
    }

    handleRotateEnd(event) {
        this.setState({
            rotateStart: this.state.rotate,
            rotateLast: this.state.rotate,
            changed: true
        })
    }

    handlePinchStart(event) {
        this.setState({
            scaleStart: event.scale,
            scaleLast: this.state.scale,
            changed: true
        })
    }

    handlePinch(event) {
        this.setState({
            scale: this.state.scaleLast - this.state.scaleStart + event.scale,
            changed: true
        })
    }

    handlePinchEnd(event) {
        this.setState({
            scaleStart: this.state.scale,
            scaleLast: this.state.scale,
            changed: true
        })
    }

    handleNextClick(event) {
        if (this.props.onNextClick) {
            this.props.onNextClick()
        }
    }

    render() {
        const { path, floor, lastFloor, polygons, pictograms = [] } = this.props
        const { width, height, offsetX, offsetY, rotate, centerX, centerY, scale } = this.state
        const viewBox = `0 0 ${width} ${height}`
        const translateTransform = `translate(${offsetX},${offsetY})`
        const rotateTransform = `rotate(${rotate} ${width/2},${height/2})`
        const scaleTransform = `translate(${centerX},${centerY}) scale(${scale}) translate(${-centerX},${-centerY})`

        const points = path
            .filter(item => item.point)
            .map(item => ({
                ...item.point,
                id: item.shopId,
                image: item.image
            }))

        if (points.length === 0) {
            return null
        }

        const startPoint = points[0]
        const startPolygon = polygons[startPoint.id]

        const endPoint = points[points.length - 1]
        const personTransform = `
            translate(${startPoint.x - 30},${startPoint.y + 4})
            rotate(${-rotate} 30 0)
            scale(${1 / scale})
        `
        const nextTransform = `
            translate(${endPoint.x},${endPoint.y})
            rotate(${-rotate} 25 0)
            scale(${1 / scale})
        `

        const finalPoint = path[path.length - 1]
        const finalPolygon = polygons[endPoint.id]

        const logos = floor.polygons
            .filter(id => polygons[id].image)
            .filter(id => (polygons[id].width * polygons[id].height) * scale > 80 * 80)
            .map(id => ({
                id: id,
                image: polygons[id].image,
                centerX: polygons[id].centerX,
                centerY: polygons[id].centerY
            }))

        return (
            <div className="map-Canvas" ref={element => this.canvas = element}>
                <Hammer
                    options={{
                        recognizers: {
                            pinch: {enable: true},
                            rotate: {enable: true}
                        }
                    }}
                    onDoubleTap={::this.handleDoubleTap}
                    onPan={::this.handlePan}
                    onPanEnd={::this.handlePanEnd}
                    onRotateStart={::this.handleRotateStart}
                    onRotateMove={::this.handleRotateMove}
                    onRotateEnd={::this.handleRotateEnd}
                    onPinchStart={::this.handlePinchStart}
                    onPinch={::this.handlePinch}
                    onPinchEnd={::this.handlePinchEnd}
                    >
                    <svg width="100%" height="100%" viewBox={viewBox}>
                        <symbol id="info" viewBox="0 0 26 26">
                            <path d="M4 0h19c2.21 0 4 1.96 4 4v19c0 2.04-1.79 4-4 4H4c-2.21 0-4-1.96-4-4V4c0-2.04 1.79-4 4-4z" fill="#4bbef4" />
                            <path d="M15 22.04h-2a1 1 0 0 1-1-1.002v-7.999a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v7.999a1 1 0 0 1-1 1.001zM14 9.73c-1.106 0-2-.825-2-1.847 0-1.019.894-1.844 2-1.844 1.105 0 2 .825 2 1.844 0 1.022-.895 1.847-2 1.847z" fill="#fff" />
                        </symbol>
                        <symbol id="subway" viewBox="0 0 24 24">
                            <path d="M12 0l12 12-12 12L0 12 12 0z" fill="#fcc94d" />
                            <path d="M10.485 12.488l-2.121.029.454.437L12 16.28l.121-.087a59644.9 59644.9 0 0 0 3.515-3.675v-.03h-2.12v-3.15L12.03 11.03l-.151-.117-1.394-1.575v3.15zM9.5 7.5h1.076l1.454 1.72v-.058L13.424 7.5c.424.01.652-.01 1.076 0v4H17v1.104L12.03 18l-.181-.146L7 12.575V11.5h2.5v-4z" fill="#fff" />
                        </symbol>
                        <symbol id="lineArrow" viewBox="0 0 10 10">
                            <path d="M0 5a5 5 0 1 1 10 0A5 5 0 0 1 0 5z" fill="#fff2f4" />
                            <path d="M1.149 5.44a.463.463 0 0 0 0 .68l.714.68a.52.52 0 0 0 .713 0l2.395-2.278 2.423 2.305a.52.52 0 0 0 .713 0l.715-.68a.466.466 0 0 0 0-.68L5.328 2.14a.526.526 0 0 0-.715 0L1.15 5.44z" fill="#db1f35" />
                        </symbol>
                        <symbol id="elevator" viewBox="0 0 26 26">
                            <path d="M 4 0 L 22 0 C 24.2091 0 26 1.7909 26 4 L 26 22 C 26 24.2091 24.2091 26 22 26 L 4 26 C 1.7909 26 0 24.2091 0 22 L 0 4 C 0 1.7909 1.7909 0 4 0 Z" fill="#ff9343" />
                            <path d="M 18.6842 16.7009 L 16.4256 19.554 C 16.1958 19.9497 15.8416 20 15.5744 19.7021 C 15.536 19.6622 15.507 19.613 15.4687 19.554 L 13.3542 16.9379 L 13.2205 16.7009 L 13.1057 16.4823 C 13 16.1658 13.2486 15.9278 13.7467 15.9278 L 14.9903 15.9278 L 14.9903 8 L 16.904 8 L 16.904 15.9278 L 18.1477 15.9278 C 18.7607 15.9278 19 16.2743 18.6842 16.7009 ZM 12.7715 10.2292 C 13 10.5467 12.9235 10.8244 12.6184 10.9326 C 12.5223 10.9831 12.3888 11.0023 12.2359 11.0023 L 10.9938 11.0023 L 10.9938 19 L 9.0828 19 L 9.0828 11.0023 L 7.8408 11.0023 C 7.2389 11.0023 7 10.656 7.3051 10.2292 L 9.5606 7.3761 C 9.6556 7.2282 9.7611 7.1189 9.8852 7.0793 C 10.1241 7 10.3537 7.0987 10.5161 7.3761 L 12.6473 10.0116 L 12.7715 10.2292 Z" fill="#ffffff" />
                        </symbol>
                        <symbol id="escalator" viewBox="0 0 26 26">
                            <path d="M 4 0 L 22 0 C 24.2091 0 26 1.7909 26 4 L 26 22 C 26 24.2091 24.2091 26 22 26 L 4 26 C 1.7909 26 0 24.2091 0 22 L 0 4 C 0 1.7909 1.7909 0 4 0 Z" fill="#fa4b7d" />
                            <path d="M 11.2521 19 L 9.2002 19 C 7.4356 19 6 17.577 6 15.8282 C 6 14.1025 7.3979 12.6933 9.1309 12.6572 L 14.4698 7.4013 C 14.7314 7.1431 15.0801 7 15.4502 7 L 17.7998 7 C 19.5646 7 21 8.4233 21 10.1724 C 21 11.9262 19.5646 13.3536 17.7998 13.3536 L 17.5626 13.3536 L 12.234 18.5986 C 11.9693 18.8575 11.6211 19 11.2521 19 ZM 9.2002 14.6562 C 8.5387 14.6562 8 15.1817 8 15.8282 C 8 16.4747 8.5387 17 9.2002 17 L 11.0063 17 L 16.7442 11.3536 L 17.7998 11.3536 C 18.4619 11.3536 19 10.8237 19 10.1724 C 19 9.5259 18.4619 9 17.7998 9 L 15.6964 9 L 9.9507 14.6562 L 9.2002 14.6562 Z" fill="#ffffff" />
                        </symbol>
                        <symbol id="stairs" viewBox="0 0 26 26">
                            <path d="M4 0h18a4 4 0 0 1 4 4v18a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill="#fa4b7d" />
                            <path d="M5 18v2h5v-4h4v-4h4V8h3V6h-5v4h-4v4H8v4H5z" fill="#fff" />
                        </symbol>
                        <symbol id="person" viewBox="0 0 40 32">
                            <path d="M 14 2.6667 C 5.8997 2.6667 0 9.2331 0 17.3333 C 0 25.4336 5.8997 32 14 32 L 25.3333 32 C 33.4336 32 40 25.4336 40 17.3333 C 40 9.2331 33.4336 2.6667 25.3333 2.6667 L 22.3333 2.6667 L 19.6667 0 L 17 2.6667 L 14 2.6667 L 14 2.6667 L 14 2.6667 Z" fill="#db2036" />
                            <path d="M 19.3503 10.8421 C 19.3503 11.7674 20.0585 12.5175 20.9322 12.5175 C 21.8059 12.5175 22.5141 11.7674 22.5141 10.8421 C 22.5141 9.9167 21.8059 9.1667 20.9322 9.1667 C 20.0585 9.1667 19.3503 9.9167 19.3503 10.8421 L 19.3503 10.8421 L 19.3503 10.8421 ZM 21.9605 16.1195 C 21.3277 15.1143 21.3277 14.1091 20.8531 13.2714 L 19.113 13.2714 C 18.4802 13.2714 15 15.4494 15 17.1248 C 15 17.6274 15.1582 17.7949 15.4746 17.7949 C 15.9491 17.7949 16.4237 16.4546 17.0565 15.952 C 17.3729 15.2819 18.0056 15.2819 18.4802 14.9468 C 18.4802 16.1195 18.1638 17.7949 18.1638 18.13 C 18.1638 18.6326 18.1638 18.9677 18.4802 19.3028 C 19.113 20.1405 19.904 20.6431 20.5367 21.4808 C 20.6949 21.4808 21.8023 25.1667 22.435 25.1667 C 22.9096 25.1667 22.9096 24.9991 23.0678 24.9991 L 23.0678 24.329 C 23.0678 22.9887 20.2203 18.8002 20.2203 18.2976 C 20.2203 17.7949 20.5367 16.9573 20.8531 16.2871 C 21.0113 16.6222 21.0113 17.1248 21.4859 17.2923 C 22.1187 17.7949 23.3842 18.4651 23.8587 18.4651 C 24.1751 18.4651 24.1751 18.4651 24.3333 18.2976 L 24.3333 17.6274 C 23.5424 17.1248 22.435 16.9573 21.9605 16.1195 L 21.9605 16.1195 L 21.9605 16.1195 ZM 19.113 20.9782 C 19.113 20.4756 18.1638 19.9729 17.8475 19.8054 C 17.6893 20.4756 17.6893 21.1457 17.3729 21.6483 C 16.7401 22.6536 15.6328 24.1615 15.6328 24.4966 C 15.6328 24.9991 15.7909 25.1667 16.1073 25.1667 C 16.4237 25.1667 19.113 21.6483 19.113 20.9782 L 19.113 20.9782 L 19.113 20.9782 Z" fill="#ffffff" />
                        </symbol>
                        <symbol id="next" viewBox="0 0 50 25">
                            <path d="M 12 0 L 38 0 C 44.6275 0 50 5.3725 50 12 L 50 13 C 50 19.6275 44.6275 25 38 25 L 12 25 C 5.3725 25 0 19.6275 0 13 L 0 12 C 0 5.3725 5.3725 0 12 0 Z" fill="#a57a2d" />
                            <path id="ico-arrow-right" d="M 37.8013 8.416 C 37.652 8.2486 37.3901 8.2486 37.2408 8.416 L 36.6798 9.0195 C 36.5252 9.1896 36.5252 9.4494 36.6798 9.6195 L 39.3833 12.4855 L 36.6573 15.3755 C 36.5029 15.5457 36.5029 15.8053 36.6573 15.9755 L 37.2183 16.579 C 37.3684 16.7452 37.6292 16.7452 37.7793 16.579 L 41.3478 12.785 C 41.5024 12.6138 41.5024 12.3533 41.3478 12.182 L 37.8013 8.416 Z" fill="#ffffff" />
                            <text x="8" y="16" fill="white" fontWeight="bold" fontSize="9" fontFamily="'Gotham-web', Arial, sans-serif">{Locale.map.next}</text>
                        </symbol>
                        <symbol id="toilets" viewBox="0 0 26 26">
                            <path d="M4 0h18a4 4 0 0 1 4 4v18a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill="#47bc00" />
                            <path d="M12.524 16.35h-1.338l-1.58-4.579-1.579 4.579H6.69L4.3 9.305h1.65L7.4 14.04l1.568-4.756h1.32l1.57 4.756 1.448-4.735h1.608l-2.389 7.045zm6.644-5.748c-1.18 0-2.03.981-2.03 2.18v.02c0 1.199.83 2.199 2.03 2.199.8 0 1.29-.32 1.839-.82l.978.989c-.717.77-1.52 1.25-2.866 1.25-2.06 0-3.589-1.589-3.589-3.599v-.018c0-1.99 1.498-3.618 3.647-3.618 1.32 0 2.11.438 2.759 1.078l-.979 1.13c-.541-.49-1.089-.79-1.789-.79z" fill="#fff" />
                        </symbol>
                        <symbol id="baby" viewBox="0 0 26 26">
                            <path d="M4 0h18a4 4 0 0 1 4 4v18a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill="#47bc00" />
                            <path d="M12 19v-1h9v1h-9zm7.642-2c-.526 0-.977-.293-1.202-.716a.958.958 0 0 1-.908.644h-2.988a.41.41 0 0 1-.146-.029.998.998 0 0 1-.845-.768l-.052-.064-.003-.095a1.658 1.658 0 0 0-.115-.511l-.71-.687-.914.042a.74.74 0 0 1-.75-.692.71.71 0 0 1 .194-.524.755.755 0 0 1 .52-.237l1.481-.068c.217 0 .407.093.545.236.041.027.089.044.125.079l.92.891c.041.01.088.015.137.019h.24l.276-.038.004.038h.728l-.436-.423a.6.6 0 0 1-.002-.864.661.661 0 0 1 .893-.001l1.656 1.604a.59.59 0 0 1 .148.25 1.36 1.36 0 0 1 1.204-.717c.749 0 1.358.59 1.358 1.316C21 16.41 20.39 17 19.642 17zm-7.883-2.472v.003-.003zm2.104-2.553a1.03 1.03 0 0 1-.751-.329L11.19 9.572l-1.788 3.15c-.02.523-.143 3.818-.233 4.318l-.006.025.006 3.709c.005.32-.115.627-.338.86-.222.232-.52.363-.838.367h-.02a1.208 1.208 0 0 1-1.193-1.194l-.07-2.828c-.177-2-.482-5.292-.543-5.54l.002-.001c-.049-.164-.169-.792.031-1.141l2.693-4.7c.178-.311.566-.497 1.038-.497.296 0 .584.09.85.22a1.465 1.465 0 0 1-.233-.542 1.504 1.504 0 0 1 .228-1.121 1.46 1.46 0 0 1 2.045-.398c.672.463.848 1.394.393 2.077a1.466 1.466 0 0 1-1.646.592l3.044 3.287a1.06 1.06 0 0 1-.043 1.477 1.02 1.02 0 0 1-.707.284zm-5.89 9.761v.003-.003z" fill="#fff" />
                        </symbol>
                        <symbol id="handicap" viewBox="0 0 26 26">
                            <path d="M4 0h18a4 4 0 0 1 4 4v18a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill="#47bc00" />
                            <path d="M17.497 18.722l-1.883-4.25H11.22l-.312-5.74a1.465 1.465 0 0 1-.708-1.258 1.476 1.476 0 0 1 2.95 0 1.48 1.48 0 0 1-.734 1.274l.12 2.212h2.998v1.532h-2.915l.025.448h3.942l1.666 3.76 1.134-.517.614 1.398-2.503 1.14zM10.65 11.11l.094 1.728-.081.066a3.13 3.13 0 0 0-1.159 2.441c0 1.722 1.377 3.123 3.068 3.123 1.419 0 2.677-1.023 2.99-2.432l.133-.593.92 2.075-.05.09c-.81 1.476-2.34 2.392-3.993 2.392C10.05 20 8 17.912 8 15.345c0-1.694.908-3.257 2.37-4.078l.28-.157z" fill="#fff" />
                        </symbol>
                        <symbol id="baby-bottle" viewBox="0 0 26 26">
                            <path d="M4 0h18a4 4 0 0 1 4 4v18a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill="#00aff0" /><path d="M10.138 20.302a.54.54 0 0 1-.327-.147l-3.385-3.23a.514.514 0 0 1-.159-.363.534.534 0 0 1 .14-.374l4.996-5.486a.571.571 0 0 1 .435-.188.523.523 0 0 1 .332.127l3.636 3.173a.523.523 0 0 1 .18.379.547.547 0 0 1-.153.398l-5.264 5.547a.532.532 0 0 1-.431.164zm1.673-9.432a.19.19 0 0 0-.145.063l-5.012 5.489a.177.177 0 0 0 .006.246l3.395 3.228a.177.177 0 0 0 .253-.006l5.248-5.543a.182.182 0 0 0 .05-.133.174.174 0 0 0-.06-.125l-3.625-3.176a.172.172 0 0 0-.11-.043z" fill="#fff" />
                            <g fill="#fff">
                                <path d="M10.386 19.964c-.135.144-.228.276-.361.15l-3.381-3.222c-.127-.12-.243-.446-.114-.587l2.422-2.655 3.975 3.633-2.54 2.681z" />
                                <path d="M10.108 20.342a.336.336 0 0 1-.205-.1L6.526 17.02a.804.804 0 0 1-.2-.368c-.047-.19-.019-.355.081-.464l2.538-2.786 4.243 3.87-2.671 2.816a4.688 4.688 0 0 0-.04.043c-.098.106-.208.225-.37.211zm-1.15-6.444l-2.304 2.524c-.007.007-.02.054.002.138.023.09.07.17.105.204l3.372 3.206c.024-.022.055-.056.08-.083a3.41 3.41 0 0 1 .043-.046l2.41-2.548-3.707-3.395z" />
                            </g>
                            <path d="M12.47 12.768l-1.215-1.075.198-.216 1.217 1.075-.2.216zm-.847.916l-1.202-1.079.196-.214 1.205 1.078-.199.215zm-.789.854l-1.191-1.082.194-.212 1.193 1.08-.196.214zm6.704-2.551l.026.022.084-2.145.86-.918c.524-.56.564-1.332.088-1.719l-.02-.017c-.473-.384-1.254-.224-1.769.334l-.9.975-2.109.288.025.021c-.47.03-.912.219-1.229.567-.67.736-.295 1.653.545 2.387l1.354 1.185c.865.757 1.89 1.046 2.596.3a1.67 1.67 0 0 0 .449-1.28z" fill="#fff" />
                        </symbol>
                        <symbol id="arrow-right" viewBox="0 0 12 10">
                            <path d="M 0.498 4.0089 L 8.4513 4.0089 L 6.1187 1.6129 C 5.9299 1.4175 5.9299 1.1003 6.1187 0.905 L 6.7997 0.1953 C 6.9863 0 7.2918 0 7.4805 0.1953 L 11.8133 4.6567 C 12 4.854 12 5.1711 11.8133 5.3665 L 7.5078 9.8028 C 7.3191 10 7.0136 10 6.8268 9.8028 L 6.1459 9.093 C 5.9572 8.8977 5.9572 8.5796 6.1459 8.3842 L 8.4776 5.9892 L 0.498 5.9892 C 0.2227 5.9892 0 5.7686 0 5.4941 L 0 4.504 C 0 4.2313 0.2227 4.0089 0.498 4.0089 Z" fill="#dd9d51" />
                        </symbol>
                        <symbol id="arrow-up" viewBox="0 0 10 12">
                            <path d="M 4.0089 11.5019 L 4.0089 3.5486 L 1.6129 5.8813 C 1.4175 6.0701 1.1003 6.0701 0.905 5.8813 L 0.1953 5.2004 C 0 5.0136 0 4.7082 0.1953 4.5194 L 4.6567 0.1868 C 4.854 0 5.1711 0 5.3665 0.1868 L 9.8028 4.4922 C 10 4.6809 10 4.9864 9.8028 5.1732 L 9.0931 5.8541 C 8.8977 6.0428 8.5796 6.0428 8.3843 5.8541 L 5.9892 3.5224 L 5.9892 11.5019 C 5.9892 11.7773 5.7687 12 5.4941 12 L 4.504 12 C 4.2313 12 4.0089 11.7773 4.0089 11.5019 Z" fill="#dd9d51" />
                        </symbol>
                        <g transform={translateTransform}>
                            <g transform={rotateTransform}>
                                <g transform={scaleTransform}>
                                    <path d={floor.background} key="background" fill="#d3d3d3" />
                                    {floor.polygons.map(id => <path d={polygons[id].path} key={id} fill={id == endPoint.id || id == startPoint.id ? "#FFC300" : "#9F9F9F"} />)}
                                    <g>
                                        {floor.labels && floor.labels.map((label, index) => (
                                            <g
                                                key={index}
                                                transform={`translate(${label.coords.x},${label.coords.y})`}
                                                >
                                                {label.anchor === 'middle' && (
                                                    <use
                                                        key="icon"
                                                        xlinkHref="#arrow-up"
                                                        width="10"
                                                        height="12"
                                                        x="-5"
                                                        y="-30"
                                                    />
                                                )}
                                                {label.anchor === 'start' && (
                                                    <use
                                                        key="icon"
                                                        xlinkHref="#arrow-up"
                                                        width="10"
                                                        height="12"
                                                        x="15"
                                                        y="-33"
                                                    />
                                                )}
                                                {label.anchor === 'end' && (
                                                    <use
                                                        key="icon"
                                                        xlinkHref="#arrow-right"
                                                        width="10"
                                                        height="12"
                                                        x="15"
                                                        y="-10"
                                                    />
                                                )}
                                                <text
                                                    key="text"
                                                    textAnchor={label.anchor || 'start'}>
                                                    {label.title}
                                                </text>
                                                {label.icon && (
                                                    <use
                                                        key="symbol"
                                                        xlinkHref={`#${label.icon.symbol}`}
                                                        width={`${label.icon.width}`}
                                                        height={`${label.icon.height}`}
                                                        x={`${label.icon.x}`}
                                                        y={`${label.icon.y}`}
                                                    />
                                                )}
                                            </g>
                                        ))}
                                    </g>
                                    <MapLine points={points} />
                                    {pictograms.map(pictogram => (
                                        <use
                                            xlinkHref={`#${pictogram.type}`}
                                            key={pictogram.id}
                                            x="0"
                                            y="0"
                                            width="13"
                                            height="13"
                                            transform={`translate(${pictogram.x}, ${pictogram.y}) rotate(${-rotate} 7.5 7.5)`}
                                        />
                                    ))}
                                    <g>
                                        {logos.map(item => (
                                            <image
                                                xlinkHref={item.image}
                                                x="0"
                                                y="0"
                                                key={item.id}
                                                width="50"
                                                height="50"
                                                transform={`
                                                    translate(${item.centerX - 25} ${item.centerY - 25})
                                                    rotate(${-rotate} 25 25)
                                                `}
                                            />
                                        ))}
                                    </g>
                                    {!(startPolygon && startPoint && startPolygon.image) && (
                                        <use
                                            xlinkHref="#person"
                                            x="0"
                                            y="0"
                                            width="40px"
                                            height="32px"
                                            transform={personTransform}
                                        />
                                    )}
                                    {!lastFloor && (
                                        <use
                                            xlinkHref="#next"
                                            x="-25"
                                            y="6"
                                            width="50px"
                                            height="32px"
                                            transform={nextTransform}
                                            onClick={::this.handleNextClick}
                                        />
                                    )}
                                    <g>
                                        {finalPolygon
                                            && finalPoint
                                            && finalPolygon.image
                                            && (
                                                <image
                                                    xlinkHref={finalPolygon.image}
                                                    x="0"
                                                    y="0"
                                                    width="50"
                                                    height="50"
                                                    transform={`
                                                        translate(${finalPolygon.centerX - 25} ${finalPolygon.centerY - 50})
                                                        rotate(${-rotate} 25 25)
                                                        scale(${1 / scale})
                                                    `}
                                                />
                                            )
                                        }
                                        {startPolygon
                                            && startPoint
                                            && startPolygon.image
                                            && (
                                                <image
                                                    xlinkHref={startPolygon.image}
                                                    x="0"
                                                    y="0"
                                                    width="50"
                                                    height="50"
                                                    transform={`
                                                        translate(${startPolygon.centerX - 25} ${startPolygon.centerY - 25})
                                                        rotate(${-rotate} 25 25)
                                                        scale(${1 / scale})
                                                    `}
                                                />
                                            )
                                        }
                                    </g>
                                </g>
                            </g>
                        </g>
                    </svg>
                </Hammer>
                {this.state.changed &&
                    <button type="button" className="map-Canvas-reset" onClick={::this.handleDoubleTap}>
                        {Locale.map.resetButton}
                    </button>
                }
            </div>
        )
    }
}

export default MapCanvas


// WEBPACK FOOTER //
// ./components/MapCanvas.js